import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { http, type ApiEnvelope } from '@/lib/http';
import { session } from '@/lib/session';
import { authService } from '@/services/auth';
import { env } from '@/lib/env';
import type { CurrentUser } from '@/types/auth';
import {
  isOnCanonicalLoginOrigin,
  redirectToPlatformLogin,
} from '@/lib/platform-login';
import { PageLoading } from '@/components/common/LoadingState';
import { ProductGate } from './ProductGate';

type Phase = 'checking' | 'authed' | 'anon';

/**
 * Gate that protects authenticated routes.
 *
 * 1. Cached user in localStorage → render immediately.
 * 2. No local session on this origin → attempt a cross-subdomain SSO
 *    bootstrap: POST /auth/refresh rides the `.zafroi.com` HttpOnly cookie
 *    (set by /auth/login on any *.zafroi.com app), then GET /auth/me seeds
 *    the session. This is what lets a user who signed in on engage- or
 *    workforce-app switch INTO Operations without being bounced to /login.
 *    (localStorage is per-origin, so the disk token isn't shared — only the
 *    cookie is.)
 * 3. `VITE_API_KEY` set → render (dev / integration fallback).
 * 4. Otherwise → /login (or hard-redirect to the canonical login origin).
 */
export function RequireAuth() {
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>(() =>
    session.cachedUser() !== null ? 'authed' : 'checking',
  );

  // Logout / refresh-failure flips us back to anon.
  useEffect(() => {
    return session.subscribe((s) => {
      if (s !== null || session.cachedUser() !== null) setPhase('authed');
    });
  }, []);

  useEffect(() => {
    if (session.cachedUser() !== null || session.current() !== null) {
      setPhase('authed');
      // Render immediately from the cached user, but refresh it in the
      // background. Tenant-level data — feature flags (which gate the nav),
      // plan, branding — can change server-side after login; without this a
      // normal reload would keep showing login-time data until the user
      // re-authenticated. Failures are non-fatal: keep the cached user.
      void authService
        .me()
        .then((fresh) => session.updateUser(fresh))
        .catch(() => undefined);
      return;
    }
    if (env.apiKey) {
      setPhase('authed');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const diskToken = session.getRefreshToken();
        // `{}` body → backend reads the .zafroi.com cookie (SSO). Disk token
        // sent as a fallback for same-origin returning users.
        const refreshed = await http.post<{
          accessToken: string;
          refreshToken: string;
          expiresAt: string;
        }>(
          '/api/v1/auth/refresh',
          diskToken ? { refreshToken: diskToken } : {},
        );
        // Session isn't seeded yet, so the request interceptor can't attach
        // the token — pass it explicitly for this one hydrate call.
        const meRes = await http.raw.get<ApiEnvelope<CurrentUser>>(
          '/api/v1/auth/me',
          { headers: { Authorization: `Bearer ${refreshed.accessToken}` } },
        );
        const user = meRes.data?.data;
        if (!user) throw new Error('No current user');
        if (cancelled) return;
        session.set({
          accessToken: refreshed.accessToken,
          refreshToken: refreshed.refreshToken,
          expiresAt: refreshed.expiresAt,
          user,
        });
        setPhase('authed');
      } catch {
        if (!cancelled) setPhase('anon');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase === 'authed') return <ProductGate />;
  if (phase === 'checking') return <PageLoading label="Restoring session…" />;

  // anon — the ERP bundle is served at multiple origins (zafroi.com,
  // www.zafroi.com, erp.zafroi.com). On the canonical origin render the local
  // /login form; on subdomains hard-redirect so there's one sign-in URL.
  if (!isOnCanonicalLoginOrigin()) {
    redirectToPlatformLogin();
    return <PageLoading label="Redirecting to sign-in…" />;
  }
  return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
}
